import React, { useMemo } from "react";
import { Box, Skeleton, Typography, alpha } from "@mui/material";
import {
  People as PeopleIcon,
  CreditCard as CreditCardIcon,
  Receipt as ReceiptIcon,
  AccountBalanceWallet as WalletIcon,
  Nfc as NfcIcon,
  Payment as PaymentIcon,
  ShoppingCart as ShoppingCartIcon,
  TrendingUp as TrendingUpIcon,
  CalendarMonth as CalendarIcon,
  Assessment as AssessmentIcon,
  PointOfSale as PointOfSaleIcon,
  TableBar as TableBarIcon,
  ShowChart as ChartIcon,
  Payments as PaymentsIcon,
  Storefront as StorefrontIcon,
} from "@mui/icons-material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { format, subDays } from "date-fns";
import reportAPI, {
  type SalesSummaryReport,
} from "../../services/reportAPI";
import { cardHolderAPI } from "../../services/cardHolderAPI";
import { userAPI } from "../../services/userAPI";
import { useAuthStore } from "../../stores/authStore";
import { useRBAC } from "../../hooks/useRBAC";
import type { Module } from "../../utils/rolePermissions";
import "./DashboardPage.css";

const PAYMENT_COLORS = ["#ea580c", "#3b82f6", "#10b981", "#8b5cf6", "#64748b", "#f59e0b"];

const formatCurrency = (value: number | string) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const formatNumber = (value: number | string) =>
  new Intl.NumberFormat("en-IN").format(Number(value) || 0);

const paymentLabel = (raw: string) => {
  const m = String(raw || "other").toLowerCase();
  if (m === "cash") return "Cash";
  if (m === "upi") return "UPI";
  if (m === "card" || m === "nfc_card") return "NFC Card";
  if (m === "online") return "Online";
  return m.replace(/_/g, " ");
};

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { canRead, hasAnyRole, canPerformRecharge } = useRBAC();
  const isFieldStaff = hasAnyRole("OPERATOR", "MERCHANT");
  const showRecharge = canPerformRecharge();
  const isAdmin = String(user?.role || "").toUpperCase() === "ADMIN";
  const branchId = isAdmin ? user?.branchId || undefined : undefined;

  const today = format(new Date(), "yyyy-MM-dd");
  const weekStart = format(subDays(new Date(), 6), "yyyy-MM-dd");
  const dateRangeLabel = `${format(subDays(new Date(), 6), "dd/MM/yyyy")} - ${format(
    new Date(),
    "dd/MM/yyyy"
  )}`;

  const canSales =
    canRead("sales") || canRead("reports") || canRead("transactions");

  const { data: salesToday, isLoading: salesTodayLoading } = useQuery({
    queryKey: ["dashboardSalesToday", today, branchId],
    queryFn: () =>
      reportAPI.getSalesReport({
        startDate: today,
        endDate: today,
        branchId,
        pageNo: 1,
        pageSize: 8,
        sortBy: "createdAt",
        sortOrder: "DESC",
      }),
    enabled: canSales,
  });

  const { data: salesWeek, isLoading: salesWeekLoading } = useQuery({
    queryKey: ["dashboardSalesWeek", weekStart, today, branchId],
    queryFn: () =>
      reportAPI.getSalesReport({
        startDate: weekStart,
        endDate: today,
        branchId,
        pageNo: 1,
        pageSize: 1,
        sortBy: "createdAt",
        sortOrder: "DESC",
      }),
    enabled: canSales,
  });

  const { data: salesSummary, isLoading: summaryLoading } = useQuery({
    queryKey: ["dashboardSalesSummary", weekStart, today, branchId],
    queryFn: () =>
      reportAPI.getReport({
        reportType: "sales-summary",
        startDate: weekStart,
        endDate: today,
        branchId,
      }) as Promise<SalesSummaryReport>,
    enabled: canSales,
  });

  const { data: cardHolderStats, isLoading: holderLoading } = useQuery({
    queryKey: ["cardHolderStats"],
    queryFn: cardHolderAPI.getCardHolderStats,
    enabled: canRead("cardholders") || canRead("nfc_cards"),
  });

  const { data: userStats, isLoading: usersLoading } = useQuery({
    queryKey: ["userStats"],
    queryFn: () => userAPI.getUsers({ pageSize: 1, pageNo: 1 }),
    enabled: canRead("users"),
  });

  const statsLoading = salesTodayLoading || salesWeekLoading;
  const firstName = user?.name?.split(" ")[0] || "Admin";
  const roleLabel = user?.role
    ? String(user.role).replace(/_/g, " ")
    : "Admin";

  const todaySummary = salesToday?.summary;
  const weekSummary = salesWeek?.summary;
  const todayBills = Number(todaySummary?.totalSales ?? 0);
  const todayNet = Number(todaySummary?.totalNet ?? 0);
  const todayAvg = todayBills > 0 ? todayNet / todayBills : 0;
  const weekNet = Number(weekSummary?.totalNet ?? 0);
  const weekBills = Number(weekSummary?.totalSales ?? 0);
  const cancelledToday = Number(salesToday?.cancelledSummary?.totalCancelled ?? 0);

  const dailySalesTrend = useMemo(() => {
    const rows = (salesSummary as SalesSummaryReport | undefined)?.dailySales || [];
    return rows.map((row) => {
      const date = String(row.date || "").slice(0, 10);
      return {
        label: date.length >= 10 ? date.slice(5) : date,
        amount: Number(row.totalAmount) || 0,
        bills: Number(row.transactionCount) || 0,
      };
    });
  }, [salesSummary]);

  const paymentSplit = useMemo(() => {
    const rows = salesWeek?.byPaymentMethod || [];
    return rows
      .map((row, i) => ({
        name: paymentLabel(row.paymentMethod),
        value: Number(row.totalNet) || 0,
        count: Number(row.saleCount) || 0,
        color: PAYMENT_COLORS[i % PAYMENT_COLORS.length],
      }))
      .filter((d) => d.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [salesWeek]);

  const topSalesPoints = useMemo(() => {
    return [...(salesWeek?.bySalesPoint || [])]
      .sort((a, b) => Number(b.totalNet) - Number(a.totalNet))
      .slice(0, 5);
  }, [salesWeek]);

  const recentSales = salesToday?.sales || [];

  const quickLinks: Array<{
    label: string;
    path: string;
    module: Module;
    icon: React.ReactNode;
  }> = [
    {
      label: "Sales Report",
      path: "/reports/sales",
      module: "reports",
      icon: <AssessmentIcon fontSize="small" />,
    },
    {
      label: "POS / Sales",
      path: "/sales",
      module: "sales",
      icon: <PointOfSaleIcon fontSize="small" />,
    },
    {
      label: hasAnyRole("OPERATOR") ? "Sales Point Products" : "Product List",
      path: "/product-list",
      module: "product_sales_points",
      icon: <ShoppingCartIcon fontSize="small" />,
    },
    {
      label: hasAnyRole("OPERATOR") ? "SalesPoint Tables" : "Table Setup",
      path: "/tables",
      module: "tables",
      icon: <TableBarIcon fontSize="small" />,
    },
    ...(isFieldStaff
      ? [
          {
            label: "My Reports",
            path: "/my-reports",
            module: "reports" as Module,
            icon: <AssessmentIcon fontSize="small" />,
          },
        ]
      : []),
    {
      label: "Quick Recharge",
      path: "/quick-recharge",
      module: "recharges",
      icon: <PaymentIcon fontSize="small" />,
    },
    {
      label: "Card Holders",
      path: "/cardholders",
      module: "cardholders",
      icon: <CreditCardIcon fontSize="small" />,
    },
    {
      label: "Transactions",
      path: "/transactions",
      module: "transactions",
      icon: <ReceiptIcon fontSize="small" />,
    },
  ];

  const visibleQuickLinks = quickLinks.filter((l) => {
    if (l.path === "/quick-recharge") {
      return showRecharge && canRead(l.module);
    }
    return canRead(l.module);
  });

  const kpiCards = [
    {
      key: "revenue",
      tone: "orange",
      icon: PaymentsIcon,
      label: "Today’s Sales",
      value: formatCurrency(todayNet),
      meta: `${formatNumber(todayBills)} bill${todayBills === 1 ? "" : "s"}`,
      onClick: canRead("reports")
        ? () => navigate("/reports/sales")
        : canRead("sales")
          ? () => navigate("/sales")
          : undefined,
    },
    {
      key: "bills",
      tone: "blue",
      icon: ReceiptIcon,
      label: "Today’s Bills",
      value: formatNumber(todayBills),
      meta:
        cancelledToday > 0
          ? `${cancelledToday} cancelled`
          : "Completed sales",
      onClick: canRead("reports")
        ? () => navigate("/reports/sales")
        : undefined,
    },
    {
      key: "avg",
      tone: "green",
      icon: TrendingUpIcon,
      label: "Avg Bill Value",
      value: formatCurrency(todayAvg),
      meta: "Today completed",
      onClick: undefined,
    },
    {
      key: "week",
      tone: "purple",
      icon: StorefrontIcon,
      label: "Last 7 Days",
      value: formatCurrency(weekNet),
      meta: `${formatNumber(weekBills)} bills`,
      onClick: canRead("reports")
        ? () => navigate("/reports/sales")
        : undefined,
    },
  ];

  const chartTooltipStyle = {
    borderRadius: 8,
    border: "1px solid #e2e8f0",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    fontSize: 12,
  };

  return (
    <div className="admin-home">
      <div className="admin-home-chrome">
        <div className="admin-home-titlebar">
          <div className="admin-home-titlebar-left">
            <span className="admin-home-app-name">Dashboard</span>
            <span className="admin-home-title-sep" />
            <span className="admin-home-title-meta">
              Welcome, {firstName} · {roleLabel} · Today{" "}
              {formatCurrency(todayNet)} sales
            </span>
          </div>
          <div className="admin-home-titlebar-right">
            <CalendarIcon sx={{ fontSize: 15, color: "#6b7280" }} />
            <span className="admin-home-title-meta">{dateRangeLabel}</span>
          </div>
        </div>
        <div className="admin-home-toolbar">
          {canRead("sales") && (
            <button
              type="button"
              className="admin-home-toolbar-btn admin-home-toolbar-btn--primary"
              onClick={() => navigate("/sales")}
            >
              <PointOfSaleIcon fontSize="small" />
              Open Sales
            </button>
          )}
          {canRead("reports") && (
            <button
              type="button"
              className="admin-home-toolbar-btn"
              onClick={() => navigate("/reports/sales")}
            >
              <AssessmentIcon fontSize="small" />
              Sales Report
            </button>
          )}
          <div className="admin-home-toolbar-spacer" />
          <span className="admin-home-toolbar-hint">
            Sales-first summary · NFC metrics secondary
          </span>
        </div>
      </div>

      <div className="admin-home-workspace">
        <section className="admin-home-kpi-row" aria-label="Sales metrics">
          {kpiCards.map((card) => {
            const Icon = card.icon;
            const body = (
              <>
                <div className="admin-home-kpi-top">
                  <span className="admin-home-kpi-label">{card.label}</span>
                  <span className="admin-home-kpi-icon">
                    <Icon fontSize="inherit" />
                  </span>
                </div>
                <div className="admin-home-kpi-value">
                  {statsLoading ? "…" : card.value}
                </div>
                <div className="admin-home-kpi-meta">{card.meta}</div>
              </>
            );
            if (card.onClick) {
              return (
                <button
                  key={card.key}
                  type="button"
                  className={`admin-home-kpi-card admin-home-kpi-card--${card.tone}`}
                  onClick={card.onClick}
                >
                  {body}
                </button>
              );
            }
            return (
              <article
                key={card.key}
                className={`admin-home-kpi-card admin-home-kpi-card--${card.tone}`}
              >
                {body}
              </article>
            );
          })}
        </section>

        {visibleQuickLinks.length > 0 && (
          <section className="admin-home-panel">
            <div className="admin-home-panel-header">Quick launch</div>
            <div className="admin-home-launcher">
              {visibleQuickLinks.map((link) => (
                <button
                  type="button"
                  key={link.path}
                  className="admin-home-launch-btn"
                  onClick={() => navigate(link.path)}
                >
                  <span className="admin-home-launch-icon">{link.icon}</span>
                  <span className="admin-home-launch-label">{link.label}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        <div className="admin-home-insight-row">
          <div className="admin-home-insight">
            <div className="admin-home-insight-label">Today’s Tax</div>
            <div className="admin-home-insight-value">
              {statsLoading
                ? "…"
                : formatCurrency(todaySummary?.totalTax ?? 0)}
            </div>
            <div className="admin-home-insight-meta">From completed sales</div>
          </div>
          <div className="admin-home-insight">
            <div className="admin-home-insight-label">Top Sales Point (7d)</div>
            <div className="admin-home-insight-value" style={{ fontSize: "1rem" }}>
              {salesWeekLoading
                ? "…"
                : topSalesPoints[0]?.salesPointName || "—"}
            </div>
            <div className="admin-home-insight-meta">
              {topSalesPoints[0]
                ? formatCurrency(topSalesPoints[0].totalNet)
                : "No sales yet"}
            </div>
          </div>
          <div className="admin-home-insight">
            <div className="admin-home-insight-label">Card Holders</div>
            <div className="admin-home-insight-value">
              {holderLoading
                ? "…"
                : formatNumber(cardHolderStats?.totalCardHolders ?? 0)}
            </div>
            <div className="admin-home-insight-meta">
              {formatNumber(cardHolderStats?.activeCardHolders ?? 0)} active · NFC
            </div>
          </div>
        </div>

        <div className="admin-home-main-grid">
          <div className="admin-home-main-col">
            <section className="admin-home-panel">
              <div className="admin-home-panel-header admin-home-panel-header--row">
                <span>
                  <ChartIcon sx={{ fontSize: 14, mr: 0.75, verticalAlign: -2 }} />
                  Daily sales (last 7 days)
                </span>
                {canRead("reports") && (
                  <button
                    type="button"
                    className="admin-home-panel-link"
                    onClick={() => navigate("/reports/sales")}
                  >
                    Full report
                  </button>
                )}
              </div>
              <div className="admin-home-panel-body" style={{ height: 300 }}>
                {summaryLoading ? (
                  <Skeleton variant="rounded" height="100%" />
                ) : dailySalesTrend.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={dailySalesTrend}
                      margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#e2e8f0"
                      />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 11, fill: "#6b7280" }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: "#6b7280" }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        contentStyle={chartTooltipStyle}
                        formatter={(v: number, name: string) =>
                          name === "bills"
                            ? [formatNumber(v), "Bills"]
                            : [formatCurrency(v), "Sales"]
                        }
                      />
                      <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                      <Bar
                        dataKey="amount"
                        name="Sales"
                        fill="#ea580c"
                        radius={[4, 4, 0, 0]}
                        maxBarSize={40}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="admin-home-empty">No sales in this period</div>
                )}
              </div>
              <div
                className="admin-home-panel-body"
                style={{
                  display: "flex",
                  gap: 28,
                  borderTop: "1px solid #eef2f6",
                  paddingTop: 12,
                }}
              >
                <div>
                  <Typography variant="caption" color="text.secondary">
                    7-day net
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    fontWeight={800}
                    sx={{ color: "#ea580c" }}
                  >
                    {formatCurrency(weekNet)}
                  </Typography>
                </div>
                <div>
                  <Typography variant="caption" color="text.secondary">
                    7-day bills
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    fontWeight={800}
                    sx={{ color: "#0f172a" }}
                  >
                    {formatNumber(weekBills)}
                  </Typography>
                </div>
                <div>
                  <Typography variant="caption" color="text.secondary">
                    Today tax
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    fontWeight={800}
                    sx={{ color: "#059669" }}
                  >
                    {formatCurrency(todaySummary?.totalTax ?? 0)}
                  </Typography>
                </div>
              </div>
            </section>

            <section className="admin-home-panel">
              <div className="admin-home-panel-header admin-home-panel-header--row">
                <span>Top sales points (7 days)</span>
              </div>
              {salesWeekLoading ? (
                <Box sx={{ p: 2 }}>
                  <Skeleton height={36} />
                  <Skeleton height={36} />
                </Box>
              ) : topSalesPoints.length === 0 ? (
                <div className="admin-home-empty">No sales-point sales yet</div>
              ) : (
                topSalesPoints.map((sp) => (
                  <div key={sp.salesPointId} className="admin-home-recent-row">
                    <div className="admin-home-recent-meta">
                      <div className="admin-home-recent-name">
                        {sp.salesPointName}
                      </div>
                      <div className="admin-home-recent-sub">
                        {sp.branchName} · {formatNumber(sp.saleCount)} bills
                      </div>
                    </div>
                    <div className="admin-home-recent-amt is-debit">
                      {formatCurrency(sp.totalNet)}
                    </div>
                  </div>
                ))
              )}
            </section>
          </div>

          <div className="admin-home-side-col">
            <section className="admin-home-panel">
              <div className="admin-home-panel-header">Payment mix (7 days)</div>
              <div className="admin-home-panel-body">
                {salesWeekLoading ? (
                  <Skeleton variant="rounded" height={200} />
                ) : paymentSplit.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart>
                        <Pie
                          data={paymentSplit}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={75}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {paymentSplit.map((e) => (
                            <Cell key={e.name} fill={e.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(v: number) => formatCurrency(v)}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="admin-home-holder-stats">
                      {paymentSplit.slice(0, 3).map((d) => (
                        <div className="admin-home-holder-stat" key={d.name}>
                          <strong>{formatCurrency(d.value)}</strong>
                          <span>
                            {d.name} · {formatNumber(d.count)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="admin-home-empty">No payment data</div>
                )}
              </div>
            </section>

            <section className="admin-home-panel">
              <div className="admin-home-panel-header admin-home-panel-header--row">
                <span>Recent sales (today)</span>
                {canRead("reports") && (
                  <button
                    type="button"
                    className="admin-home-panel-link"
                    onClick={() => navigate("/reports/sales")}
                  >
                    View all
                  </button>
                )}
              </div>
              {salesTodayLoading ? (
                <Box sx={{ p: 2 }}>
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} height={44} sx={{ mb: 1 }} />
                  ))}
                </Box>
              ) : recentSales.length > 0 ? (
                recentSales.map((sale) => (
                  <div
                    key={sale.id}
                    className="admin-home-recent-row"
                    onClick={() => navigate("/reports/sales")}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") navigate("/reports/sales");
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="admin-home-recent-meta">
                      <div className="admin-home-recent-name">
                        {sale.saleNumber}
                      </div>
                      <div className="admin-home-recent-sub">
                        {sale.salesPoint?.name || "—"} ·{" "}
                        {paymentLabel(sale.paymentMethod)}
                        {sale.status === "cancelled" ? " · cancelled" : ""}
                      </div>
                    </div>
                    <div
                      className={`admin-home-recent-amt ${
                        sale.status === "cancelled" ? "is-debit" : "is-credit"
                      }`}
                    >
                      {formatCurrency(sale.netAmount)}
                    </div>
                  </div>
                ))
              ) : (
                <div className="admin-home-empty">No sales today</div>
              )}
            </section>

            <section className="admin-home-panel">
              <div className="admin-home-panel-header">System snapshot</div>
              <div
                className="admin-home-panel-body"
                style={{ display: "flex", gap: 24, flexWrap: "wrap" }}
              >
                {canRead("users") && (
                  <div>
                    <div className="admin-home-insight-label">
                      <PeopleIcon
                        sx={{ fontSize: 14, mr: 0.5, verticalAlign: -2 }}
                      />
                      Users
                    </div>
                    <div className="admin-home-insight-value">
                      {usersLoading
                        ? "…"
                        : formatNumber(userStats?.total ?? 0)}
                    </div>
                  </div>
                )}
                {(canRead("cardholders") || canRead("nfc_cards")) && (
                  <div>
                    <div className="admin-home-insight-label">
                      <NfcIcon
                        sx={{ fontSize: 14, mr: 0.5, verticalAlign: -2 }}
                      />
                      Card holders
                    </div>
                    <div className="admin-home-insight-value">
                      {holderLoading
                        ? "…"
                        : formatNumber(
                            cardHolderStats?.totalCardHolders ?? 0
                          )}
                    </div>
                  </div>
                )}
                <div>
                  <div className="admin-home-insight-label">
                    <WalletIcon
                      sx={{ fontSize: 14, mr: 0.5, verticalAlign: -2 }}
                    />
                    Discount today
                  </div>
                  <div className="admin-home-insight-value">
                    {formatCurrency(todaySummary?.totalDiscount ?? 0)}
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
